import { useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, Users, Target, Zap, ArrowRight, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import useApiData from "../hooks/useApiData";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isPresent = (v) => v !== null && v !== undefined && v !== "";
const hasItems = (arr) => Array.isArray(arr) && arr.length > 0;

// ─── Icon Map ─────────────────────────────────────────────────────────────────

const iconMap = { target: Target, zap: Zap, users: Users, heart: Heart };

// ─── About ────────────────────────────────────────────────────────────────────

export default function About() {
  const { data: aboutData } = useApiData("/about", null);

  const hero = aboutData?.hero ?? null;
  const stats = aboutData?.stats ?? [];
  const values = aboutData?.values ?? [];
  const story = aboutData?.story ?? null;
  const cta = aboutData?.cta ?? null;

  // Title lines — handles both "Built For\nChampions" and plain single-line titles
  const titleLines = useMemo(
    () => (isPresent(hero?.title) ? hero.title.split("\n") : []),
    [hero],
  );

  if (!aboutData) return null;

  return (
    <div
      data-testid="about-page"
      className="min-h-screen bg-[var(--dvx-bg)] pt-24 transition-colors duration-300"
    >
      {/* ── Hero ── */}
      {hero && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
              >
                {isPresent(hero.badge) && (
                  <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
                    {hero.badge}
                  </span>
                )}

                {titleLines.length > 0 && (
                  <h1 className="mt-4 font-oswald text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
                    {titleLines.map((line, idx) => (
                      <span key={idx}>
                        {line}
                        {idx < titleLines.length - 1 ? <br /> : null}
                      </span>
                    ))}
                  </h1>
                )}

                {isPresent(hero.subtitle) && (
                  <p className="mt-3 text-[var(--dvx-primary)] font-oswald text-lg uppercase tracking-wide">
                    {hero.subtitle}
                  </p>
                )}

                {/* description supports both plain text and HTML */}
                {isPresent(hero.description) &&
                  (hero.description.trim().startsWith("<") ? (
                    <div
                      className="mt-6 text-[var(--dvx-text-muted)] font-manrope text-lg leading-relaxed prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: hero.description }}
                    />
                  ) : (
                    <p className="mt-6 text-[var(--dvx-text-muted)] font-manrope text-lg leading-relaxed">
                      {hero.description}
                    </p>
                  ))}

                {/* legacy plain-text second paragraph */}
                {isPresent(hero.description_2) && (
                  <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg leading-relaxed">
                    {hero.description_2}
                  </p>
                )}
              </motion.div>

              {/* Media — image or video */}
              {isPresent(hero.media) && (
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="relative"
                >
                  <div className="relative">
                    {hero.media_type === "video" ? (
                      <video
                        src={hero.media}
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls
                        className="w-full aspect-[4/5] object-cover"
                      />
                    ) : (
                      <img
                        src={hero.media}
                        alt="DVX"
                        className="w-full aspect-[4/5] object-cover"
                      />
                    )}
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[var(--dvx-primary)]" />
                    <div className="absolute -top-4 -left-4 w-24 h-24 bg-[var(--dvx-primary)]/20" />
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Stats ── */}
      {hasItems(stats) && (
        <section className="py-16 bg-[var(--dvx-surface)] transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, i) => {
                if (!isPresent(stat.value) || !isPresent(stat.label))
                  return null;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="text-center"
                  >
                    <div className="font-oswald text-4xl md:text-5xl font-bold text-[var(--dvx-primary)]">
                      {stat.value}
                    </div>
                    <div className="text-[var(--dvx-text-muted)] text-sm uppercase tracking-wider mt-2">
                      {stat.label}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Values ── */}
      {hasItems(values) && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
                Our Philosophy
              </span>
              <h2 className="mt-4 font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
                Core Values
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, i) => {
                if (!isPresent(value.title)) return null;
                const ValueIcon = iconMap[value.icon] || Target;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] p-8 text-center transition-colors duration-300"
                  >
                    <div className="w-16 h-16 mx-auto bg-[var(--dvx-primary)]/10 border border-[var(--dvx-primary)]/30 flex items-center justify-center mb-6">
                      <ValueIcon
                        className="text-[var(--dvx-primary)]"
                        size={32}
                      />
                    </div>
                    <h3 className="font-oswald text-xl font-bold text-[var(--dvx-text)] uppercase mb-3">
                      {value.title}
                    </h3>
                    {isPresent(value.description) && (
                      <p className="text-[var(--dvx-text-muted)] font-manrope text-sm">
                        {value.description}
                      </p>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Story ── (only shown if API returns it) */}
      {story &&
        (isPresent(story.title) ||
          hasItems(story.paragraphs) ||
          isPresent(story.highlight)) && (
          <section className="py-16 bg-[var(--dvx-surface)] transition-colors duration-300">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                {isPresent(story.title) && (
                  <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[var(--dvx-text)] uppercase mb-8">
                    {story.title}
                  </h2>
                )}
                <div className="space-y-6 text-[var(--dvx-text-muted)] font-manrope text-lg leading-relaxed text-left">
                  {hasItems(story.paragraphs) &&
                    story.paragraphs.map((paragraph, idx) =>
                      isPresent(paragraph) ? (
                        <p key={idx}>{paragraph}</p>
                      ) : null,
                    )}
                  {isPresent(story.highlight) && (
                    <p className="text-[var(--dvx-text)] font-semibold">
                      {story.highlight}
                    </p>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        )}

      {/* ── CTA ── */}
      {cta &&
        (isPresent(cta.title) ||
          isPresent(cta.description) ||
          isPresent(cta.button_text)) && (
          <section className="py-16 md:py-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {isPresent(cta.title) && (
                  <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[var(--dvx-text)] uppercase mb-4">
                    {cta.title}
                  </h2>
                )}
                {isPresent(cta.description) && (
                  <p className="text-[var(--dvx-text-muted)] font-manrope text-lg mb-8">
                    {cta.description}
                  </p>
                )}
                {isPresent(cta.button_text) && (
                  <Link
                    to={
                      isPresent(cta.button_href) ? cta.button_href : "/pricing"
                    }
                  >
                    <Button
                      data-testid="about-cta-btn"
                      className="rounded-none px-8 py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold uppercase tracking-widest hover:scale-105 transition-transform"
                    >
                      {cta.button_text}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                )}
              </motion.div>
            </div>
          </section>
        )}
    </div>
  );
}
